import { zodFunction } from '@/utils/zod-utils';
import {
	BuildDashboardContext,
	ChartListSummary,
	DashboardContextPromptEntry,
	DashboardSummary,
	ExtendedDashboardProps,
	buildDashboardSchema,
	createDashboardSchema,
	loadExistingFusionDashboardSchema,
	updateDashboardSchema,
	addDashboardWidgetsSchema,
	addDashboardFiltersSchema,
	createChartWidgetForDashboardSchema,
	updateChartWidgetForDashboardSchema,
} from './types';
import { ChartSummary } from '../build-chart/types';
import { createDashboard } from './create-dashboard';
import { loadExistingFusionDashboard } from './load-existing-fusion-dashboard';
import { updateDashboard } from './update-dashboard';
import { addDashboardWidgets } from './add-dashboard-widgets';
import { addDashboardFilters } from './add-dashboard-filters';
import { createChartWidgetForDashboard } from './create-chart-widget';
import { updateChartWidgetForDashboard } from './update-chart-widget';
import { getBaseDashboardSummary } from './build-dashboard-service';
import {
	TOOL_CONTEXT_MAX_ASSETS,
	TOOL_NAME_BUILD_DASHBOARD,
	toEngineName,
} from '@/constants';
import { devDebug } from '@/utils/dev-debug';
import { createToolsCompletion } from '@/utils/chat-completion-utils';
import { resetWidgetForDashboard } from '@/services/dashboards/dashboards-service';
import z from 'zod';

/**
 * Higher-order function to create a tool with bound context
 * @param originalSchema - The schema for the tool
 * @param name - The name of the tool
 * @param description - The description of the tool
 * @param originalFunction - The function to call when the tool is used
 * @param boundContext - The context for the build dashboard
 * @returns A zod function for the tool
 */
const createDashboardTool = (
	originalSchema: any,
	name: string,
	description: string,
	originalFunction: any,
	boundContext: BuildDashboardContext,
) => {
	return zodFunction({
		schema: originalSchema,
		name,
		description,
		function: async (params: any, context: any) => {
			// Merge LLM params with bound context params and dependencies
			return originalFunction(
				{ ...params },
				{ ...boundContext, ...context },
			);
		},
	});
};

/**
 * Create a set of tools for the build dashboard with injected dependencies
 * @param context - The bound context for the build dashboard.
 * @returns A set of tools for the build dashboard.
 */
const createDashboardTools = (context: BuildDashboardContext) => {
	return [
		createDashboardTool(
			loadExistingFusionDashboardSchema,
			'loadExistingFusionDashboard',
			'Load an existing Fusion dashboard by title',
			loadExistingFusionDashboard,
			context,
		),
		createDashboardTool(
			createDashboardSchema,
			'createDashboard',
			'Create a new dashboard with optional widgets',
			createDashboard,
			context,
		),
		createDashboardTool(
			updateDashboardSchema,
			'updateDashboard',
			'Update the title of an existing dashboard',
			updateDashboard,
			context,
		),
		createDashboardTool(
			addDashboardWidgetsSchema,
			'addDashboardWidgets',
			'Add existing chart or chart list to an existing dashboard',
			addDashboardWidgets,
			context,
		),
		createDashboardTool(
			createChartWidgetForDashboardSchema,
			'createChartWidgetForDashboard',
			'Create a new chart widget and add it to the dashboard',
			createChartWidgetForDashboard,
			context,
		),
		createDashboardTool(
			updateChartWidgetForDashboardSchema,
			'updateChartWidgetForDashboard',
			'Update an existing chart widget in the dashboard.',
			updateChartWidgetForDashboard,
			context,
		),
		createDashboardTool(
			addDashboardFiltersSchema,
			'addDashboardFilters',
			'Add combined filters to an existing dashboard. Do not split the prompt into multiple filters.',
			addDashboardFilters,
			context,
		),
	];
};

/**
 * Escape special characters, control bytes, and whitespace for safe prompt interpolation
 */
function escapeForPrompt(value: string): string {
	let withoutControl = '';
	for (let i = 0; i < value.length; i++) {
		const code = value.charCodeAt(i);
		if (code > 0x1f && code !== 0x7f) {
			withoutControl += value[i];
		}
	}
	return withoutControl
		.replace(/`/g, '\\`') // Escape backticks
		.replace(/\n/g, ' ') // Replace newlines with space
		.replace(/\r/g, ' ') // Replace carriage returns with space
		.replace(/\t/g, ' ') // Replace tabs with space
		.replace(/\s+/g, ' ') // Collapse multiple spaces
		.trim();
}

/**
 * Build contextual system prompt for build dashboard
 */
function buildPriorDashboardSystemPromptBlock(dashboardId: string): string {
	const sanitized = escapeForPrompt(dashboardId);
	return `You are operating on an existing dashboard (ID: ${sanitized}).
When calling any dashboard tool (addDashboardFilters, updateChartWidgetForDashboard,
updateDashboard, etc.), use ${sanitized} as the dashboardId argument.

`;
}

export function buildContextualSystemPrompt(
	chartSummaries: ChartSummary[],
	chartListSummaries: ChartListSummary[],
	dashboardsForContext: DashboardContextPromptEntry[],
	priorDashboardId?: string,
) {
	const dashboardScopeInstruction =
		priorDashboardId !== undefined && priorDashboardId !== ''
			? `You are operating on dashboard \`${escapeForPrompt(
					priorDashboardId,
			  )}\`. Its full widget inventory is provided in the context below.`
			: 'Always use the last dashboard in the context as the current dashboard unless the user prompt specifies otherwise.';

	return `You are an intelligent and proactive dashboard assistant. 
	Your role is to create, load, and update a dashboard, its widgets, and its filters based on natural language requests by using the available tools.
	
	 
	IMPORTANT INSTRUCTIONS:
	1. Use the available tools to build the dashboard. Politely decline if asked to do something not related to the available tools.
	2. If the user prompt can be handled by a single tool, use that tool without splitting the prompt into multiple steps. 
	3. Use the assets in the following context to build the dashboard: ${JSON.stringify(
		{
			charts: chartSummaries,
			chartLists: chartListSummaries,
			dashboards: dashboardsForContext,
		},
		null,
		2,
	)}
	4. Always reference the assets by their IDs instead of their titles.
    5. ${dashboardScopeInstruction}
	6. To add a widget to the dashboard, use the last chart or chart list in the context.
	7. If no charts or chart lists exist in the context, run the \`createChartWidgetForDashboard\` tool to build a chart and add it to the dashboard.
	8. To update a chart widget, make sure the widget exists within the dashboard.
	`;
}

/**
 * Standalone build dashboard engine - completely decoupled from any UI or store
 * @param params - The parameters for the build dashboard
 * @param context - All external dependencies injected
 * @returns The dashboard summary
 */
export async function buildDashboardEngine(
	params: z.infer<typeof buildDashboardSchema>,
	context: BuildDashboardContext,
): Promise<DashboardSummary> {
	const { dataSourceTitle, userPrompt } = params;
	const effectivePriorDashboardId =
		params.priorDashboardId?.trim() || undefined;

	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_BUILD_DASHBOARD),
		dataSourceTitle,
		userPrompt,
		effectivePriorDashboardId,
	});

	if (!userPrompt) {
		throw new Error(
			'Error: Please ask the user to specify textual user input',
		);
	}

	// use the tool call id as the new dashboard id
	const newDashboardId = context.toolCallId;

	let savedDashboardProps: ExtendedDashboardProps | null = null;

	let message = '';
	let isError = false;

	const updatedContext: BuildDashboardContext = {
		...context,
		dataSourceTitle,
		retrieveDashboard: (dashboardId: string) => {
			// Prioritize savedDashboardProps in case of subsequent tool calls
			if (savedDashboardProps) {
				return savedDashboardProps;
			}
			if (dashboardId && context.retrieveDashboard) {
				return context.retrieveDashboard(dashboardId);
			}
			return null;
		},
		saveDashboard: (dashboardProps: ExtendedDashboardProps) => {
			context.saveDashboard?.(dashboardProps);
			savedDashboardProps = dashboardProps;
		},
		getWidgetList: (widgetIds: string[]) => {
			if (context.getWidgetList) {
				return context
					.getWidgetList(widgetIds)
					.map(resetWidgetForDashboard);
			}
			return [];
		},
	};

	// Update dashboard summaries with latest changes from context
	// (dashboard tool call results may be manually updated by the user using the UI)
	const updatedDashboardSummaries: DashboardSummary[] = (
		context.dashboardSummaries ?? []
	).map((dashboardSummary) => {
		if (!dashboardSummary.dashboard) {
			return dashboardSummary;
		}

		if (context.retrieveDashboard) {
			try {
				const dashboardProps = context.retrieveDashboard(
					dashboardSummary.dashboardId,
				);
				if (dashboardProps) {
					return {
						...dashboardSummary,
						dashboard: getBaseDashboardSummary(
							dashboardProps,
							dashboardSummary.dashboard
								?.defaultDataSourceTitle || dataSourceTitle,
						),
					};
				}
			} catch {
				// If loading fails, return original summary
			}
		}
		return dashboardSummary;
	});

	const targetedDashboardProps =
		effectivePriorDashboardId && context.retrieveDashboard
			? context.retrieveDashboard(effectivePriorDashboardId)
			: null;

	const dashboardsForPrompt: DashboardContextPromptEntry[] =
		effectivePriorDashboardId && targetedDashboardProps
			? [
					{
						dashboardId: effectivePriorDashboardId,
						dashboard: getBaseDashboardSummary(
							targetedDashboardProps,
							dataSourceTitle,
						),
					},
			  ]
			: updatedDashboardSummaries.slice(-TOOL_CONTEXT_MAX_ASSETS);

	const contextualPrompt = buildContextualSystemPrompt(
		(context.chartSummaries ?? []).slice(-TOOL_CONTEXT_MAX_ASSETS),
		// only use the latest chart list summary so that the LLM can focus on the latest recommendations
		(context.chartListSummaries ?? []).slice(-1),
		dashboardsForPrompt,
		effectivePriorDashboardId,
	);

	const systemPrompt =
		(effectivePriorDashboardId && targetedDashboardProps
			? buildPriorDashboardSystemPromptBlock(effectivePriorDashboardId)
			: '') + contextualPrompt;

	const internalTools = createDashboardTools(updatedContext);

	// Use createToolsCompletion to let LLM choose and execute the appropriate sub-tool
	const { combinedMessage, isError: isErrorInToolsCompletion } =
		await createToolsCompletion({
			systemPrompt,
			userPrompt,
			tools: internalTools,
			openAIClient: context.openAIClient,
		});

	message = combinedMessage;
	isError = isErrorInToolsCompletion;

	if (isError) {
		throw new Error(message);
	}

	const resultForLLM: DashboardSummary = {
		message,
		createdAt: new Date().toISOString(),
		dashboardId: newDashboardId,
		dashboard: savedDashboardProps
			? getBaseDashboardSummary(savedDashboardProps, dataSourceTitle)
			: undefined,
	};

	return resultForLLM;
}
